import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ArrowRight, HelpCircle, Book, FileText, MessageSquare, Mail, School, CreditCard, Phone } from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { useScrollToTop } from '../hooks/useScrollToTop';

const HelpCenter: React.FC = () => {
  useScrollToTop();
  const [searchQuery, setSearchQuery] = useState('');

  const helpCategories = [
    {
      title: 'Getting Started',
      icon: <Book className="h-6 w-6 text-[#7B4AE2]" />,
      description: 'Learn the basics of using 11th Admissions',
      articles: [
        { title: 'Creating your account', slug: 'creating-account' },
        { title: 'Uploading your marksheet', slug: 'uploading-marksheet' },
        { title: 'Understanding your dashboard', slug: 'understanding-dashboard' },
        { title: 'Finding colleges', slug: 'finding-colleges' }
      ]
    },
    {
      title: 'College Selection',
      icon: <School className="h-6 w-6 text-[#18CDCA]" />,
      description: 'Tips for choosing the right colleges',
      articles: [
        { title: 'How to compare colleges', slug: 'comparing-colleges' },
        { title: 'Understanding cutoffs', slug: 'understanding-cutoffs' },
        { title: 'College rankings explained', slug: 'college-rankings' },
        { title: 'Selecting your stream', slug: 'selecting-stream' }
      ]
    },
    {
      title: 'Admission Process',
      icon: <FileText className="h-6 w-6 text-[#FFD166]" />,
      description: 'Navigate the admission process smoothly',
      articles: [
        { title: 'Government Directives for 11th Admissions (2025-26)', slug: 'government-directives-2025', isNew: true },
        { title: 'Application timeline', slug: 'application-timeline' },
        { title: 'Required documents', slug: 'required-documents' },
        { title: 'Merit list process', slug: 'merit-list-process' },
        { title: 'Admission confirmation', slug: 'admission-confirmation' }
      ]
    },
    {
      title: 'Account & Billing',
      icon: <CreditCard className="h-6 w-6 text-[#EF476F]" />,
      description: 'Manage your account and subscription',
      articles: [
        { title: 'Premium features', slug: 'premium-features' },
        { title: 'Payment methods', slug: 'payment-methods' },
        { title: 'Cancellation policy', slug: 'cancellation-policy' },
        { title: 'Account security', slug: 'account-security' }
      ]
    }
  ];

  const popularArticles = [
    { title: 'Government Directives for 11th Admissions (2025-26)', slug: 'government-directives-2025', isNew: true },
    { title: 'How to interpret your college matches', slug: 'interpret-college-matches' },
    { title: 'Understanding cutoff predictions', slug: 'cutoff-predictions' },
    { title: 'Tips for selecting the right stream', slug: 'stream-selection-tips' },
    { title: 'Common admission mistakes to avoid', slug: 'admission-mistakes' }
  ];

  const filteredCategories = searchQuery 
    ? helpCategories.map(category => ({
        ...category,
        articles: category.articles.filter(article => 
          article.title.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(category => category.articles.length > 0)
    : helpCategories;

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          {/* Hero Section */}
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">How can we help you?</h1>
            <p className="text-lg text-gray-600 mb-8">
              Find answers to common questions about 11th Admissions
            </p>
            
            <div className="relative max-w-2xl mx-auto">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for help articles..."
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#7B4AE2] focus:border-[#7B4AE2] transition duration-150 ease-in-out"
              />
            </div>
          </div>
          
          {/* Help Categories */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {filteredCategories.map((category, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-lg bg-[#F0E9FF] flex items-center justify-center mr-4">
                    {category.icon}
                  </div>
                  <h3 className="text-lg font-semibold">{category.title}</h3>
                </div>
                <p className="text-gray-600 text-sm mb-4">{category.description}</p>
                <ul className="space-y-2">
                  {category.articles.map((article, idx) => (
                    <li key={idx}>
                      <Link 
                        to={`/help/article/${article.slug}`} 
                        className="text-[#7B4AE2] hover:text-[#6A3ACF] text-sm flex items-center"
                      >
                        <ArrowRight className="h-3 w-3 mr-2" />
                        {article.title}
                        {article.isNew && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            New
                          </span>
                        )}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          
          {/* Popular Articles */}
          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-2xl font-bold mb-6 text-center">Popular Articles</h2>
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="grid md:grid-cols-2 gap-4">
                {popularArticles.map((article, index) => (
                  <Link 
                    key={index} 
                    to={`/help/article/${article.slug}`}
                    className="p-4 border border-gray-100 rounded-lg hover:bg-[#F0E9FF] transition-colors relative"
                  >
                    <div className="flex items-start">
                      <FileText className="h-5 w-5 text-[#7B4AE2] mt-0.5 mr-3" />
                      <span className="text-gray-800">{article.title}</span>
                    </div>
                    {article.isNew && (
                      <span className="absolute top-2 right-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                        New
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          </div>
          
          {/* Contact Support */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-[#F0E9FF] rounded-lg p-8 text-center">
              <h2 className="text-2xl font-bold mb-4">Still need help?</h2>
              <p className="text-gray-600 mb-6">
                Our support team is ready to assist you with any questions or issues
              </p>
              <div className="flex flex-col md:flex-row justify-center gap-4">
                <a 
                  href="https://wa.me/918605194311" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-[#7B4AE2] text-white rounded-lg hover:bg-[#6A3ACF] transition-colors"
                >
                  <Phone className="h-5 w-5 mr-2" />
                  Call Rishi
                </a>
                <Link 
                  to="/contact" 
                  className="inline-flex items-center justify-center px-6 py-3 border border-[#7B4AE2] text-[#7B4AE2] rounded-lg hover:bg-[#F0E9FF] transition-colors"
                >
                  <Mail className="h-5 w-5 mr-2" />
                  Contact Support
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default HelpCenter;